/**
 * H2 — Client-side conversion funnel tracking.
 *
 * Tracks a visitor's progression through the buying funnel and computes
 * a conversion score used by:
 *   - Sticky mobile CTA (intensifies for high-intent visitors)
 *   - Retargeting logic
 *   - Analytics dashboard (visitor-level funnel view)
 *
 * Stages:
 *   1. awareness    — landed on a hub or content page
 *   2. consideration — viewed a category or brand page
 *   3. evaluation   — viewed a product PDP
 *   4. intent       — clicked "Request Quote" or "Add to Cart"
 *   5. conversion   — submitted a lead form
 *
 * All events also flow through trackEvent() to the seo_events table,
 * so the server-side dashboard has full fidelity.
 */
import { trackEvent } from "@/hooks/useTrackEvent";

const FUNNEL_KEY = "nardy_funnel_state";

export type FunnelStage =
  | "awareness"
  | "consideration"
  | "evaluation"
  | "intent"
  | "conversion";

const STAGE_ORDER: FunnelStage[] = [
  "awareness",
  "consideration",
  "evaluation",
  "intent",
  "conversion",
];

interface FunnelState {
  stage: FunnelStage;
  score: number; // 0-100
  productsViewed: string[];
  brandsViewed: string[];
  categoriesViewed: string[];
  firstSeen: number;
  lastSeen: number;
  quoteClicks: number;
  searches: number;
}

const emptyState = (): FunnelState => ({
  stage: "awareness",
  score: 0,
  productsViewed: [],
  brandsViewed: [],
  categoriesViewed: [],
  firstSeen: Date.now(),
  lastSeen: Date.now(),
  quoteClicks: 0,
  searches: 0,
});

const load = (): FunnelState => {
  if (typeof window === "undefined") return emptyState();
  try {
    const raw = window.localStorage.getItem(FUNNEL_KEY);
    if (!raw) return emptyState();
    return { ...emptyState(), ...JSON.parse(raw) };
  } catch {
    return emptyState();
  }
};

const save = (state: FunnelState) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FUNNEL_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
};

const advance = (state: FunnelState, target: FunnelStage): FunnelState => {
  const currentIdx = STAGE_ORDER.indexOf(state.stage);
  const targetIdx = STAGE_ORDER.indexOf(target);
  if (targetIdx > currentIdx) state.stage = target;
  return state;
};

const recomputeScore = (state: FunnelState): number => {
  let s = 0;
  s += Math.min(state.productsViewed.length * 8, 30);
  s += Math.min(state.brandsViewed.length * 4, 15);
  s += Math.min(state.categoriesViewed.length * 3, 10);
  s += Math.min(state.searches * 3, 10);
  s += Math.min(state.quoteClicks * 12, 25);
  s += STAGE_ORDER.indexOf(state.stage) * 2;
  return Math.min(100, Math.round(s));
};

const pushUnique = (arr: string[], v: string, max = 20): string[] => {
  if (!v) return arr;
  const next = arr.filter((x) => x !== v);
  next.push(v);
  return next.slice(-max);
};

/** Record a hub/content page view — moves visitor to "awareness". */
export function recordHubView(hub: string) {
  const state = load();
  advance(state, "awareness");
  state.lastSeen = Date.now();
  state.score = recomputeScore(state);
  save(state);
  trackEvent("funnel_progress", { stage: state.stage, score: state.score, hub });
}

/** Record a category page view — moves visitor to "consideration". */
export function recordCategoryView(categorySlug: string) {
  const state = load();
  advance(state, "consideration");
  state.categoriesViewed = pushUnique(state.categoriesViewed, categorySlug);
  state.lastSeen = Date.now();
  state.score = recomputeScore(state);
  save(state);
  trackEvent("funnel_progress", { stage: state.stage, score: state.score, category: categorySlug });
}

/** Record a brand page view — moves visitor to "consideration". */
export function recordBrandView(brandSlug: string) {
  const state = load();
  advance(state, "consideration");
  state.brandsViewed = pushUnique(state.brandsViewed, brandSlug);
  state.lastSeen = Date.now();
  state.score = recomputeScore(state);
  save(state);
  trackEvent("funnel_progress", { stage: state.stage, score: state.score, brand: brandSlug });
}

/** Record a product PDP view — moves visitor to "evaluation". */
export function recordProductView(productSlug: string) {
  const state = load();
  advance(state, "evaluation");
  state.productsViewed = pushUnique(state.productsViewed, productSlug);
  state.lastSeen = Date.now();
  state.score = recomputeScore(state);
  save(state);
  trackEvent("funnel_progress", { stage: state.stage, score: state.score, product: productSlug });
}

/** Record a quote click — moves visitor to "intent". */
export function recordQuoteIntent(source: string) {
  const state = load();
  advance(state, "intent");
  state.quoteClicks += 1;
  state.lastSeen = Date.now();
  state.score = recomputeScore(state);
  save(state);
  trackEvent("funnel_progress", { stage: state.stage, score: state.score, source });
}

/** Record a search — mild score bump. */
export function recordSearch(query: string) {
  const state = load();
  state.searches += 1;
  state.lastSeen = Date.now();
  state.score = recomputeScore(state);
  save(state);
  trackEvent("funnel_progress", { stage: state.stage, score: state.score, query });
}

/** Record a successful lead form submission — moves visitor to "conversion". */
export function recordConversion(formType: string) {
  const state = load();
  advance(state, "conversion");
  state.lastSeen = Date.now();
  state.score = 100;
  save(state);
  trackEvent("funnel_progress", { stage: state.stage, score: state.score, form: formType });
}

/** Read-only snapshot of the current funnel state. */
export function getFunnelState(): FunnelState {
  return load();
}

/** True if visitor is high-intent (evaluation+ or score >= 40). */
export function isHighIntent(): boolean {
  const s = load();
  return s.score >= 40 || STAGE_ORDER.indexOf(s.stage) >= STAGE_ORDER.indexOf("evaluation");
}